import React, { useEffect, useState } from "react";
import { api, formatApiError, fmtINR, API } from "../lib/apiClient";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";
import { FileXls, CaretDown, CaretRight, WhatsappLogo } from "@phosphor-icons/react";

const today = () => new Date().toISOString().slice(0, 10);
const BUCKETS = ["0-30", "31-60", "61-90", "90+"];

export default function OutstandingReportPage() {
  const [asOn, setAsOn] = useState(today());
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [sending, setSending] = useState("");
  const [receiveFor, setReceiveFor] = useState(null); // customer object
  const [waStatus, setWaStatus] = useState(null);
  useEffect(() => {
    api.get("/integrations/gallabox/health").then(r => setWaStatus(r.data)).catch((err) => console.warn("Gallabox health check failed:", err));
  }, []);

  const remind = async (customer, e) => {
    e.stopPropagation();
    setSending(customer);
    try {
      const { data } = await api.post("/reports/outstanding/remind", { customer }, { timeout: 60000 });
      toast.success(`WhatsApp reminder sent for ${data.invoice_number}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Reminder failed");
    } finally { setSending(""); }
  };

  const load = async () => {
    try { const { data } = await api.get("/reports/outstanding", { params: { as_on: asOn } }); setData(data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadXlsx = async () => {
    try {
      const res = await fetch(`${API}/reports/outstanding.xlsx?as_on=${asOn}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `outstanding-${asOn}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch { toast.error("Download failed"); }
  };

  return (
    <div data-testid="outstanding-page">
      <PageHeader title="Outstanding Report" subtitle="Customer dues with invoice-wise ageing buckets." />
      <div className="p-8 space-y-6">
        {waStatus && !waStatus.connected && (
          <div className="rounded-md border border-amber-400 bg-amber-50 text-amber-900 px-4 py-3 text-sm flex items-start gap-2" data-testid="gallabox-banner">
            <span className="font-bold">⚠ WhatsApp (Gallabox) disconnected —</span>
            <span>{waStatus.reason || "account unavailable"}. Payment reminders will fail until you reconnect your Gallabox account.</span>
          </div>
        )}
        <div className="card-base p-4 flex items-end gap-3 flex-wrap">
          <div><div className="label-overline mb-1">As on</div><input type="date" className="field" value={asOn} onChange={e => setAsOn(e.target.value)} data-testid="out-ason" /></div>
          <button onClick={load} className="btn-primary" data-testid="out-run">Show Outstanding</button>
          <button onClick={downloadXlsx} disabled={!data} className="btn-secondary inline-flex items-center gap-1.5" data-testid="out-xlsx"><FileXls size={14} weight="bold" /> Download Excel</button>
        </div>

        {data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <Kpi label="Total Due" value={fmtINR(data.grand.total_due)} accent />
              <Kpi label="Customers" value={data.grand.customers} />
              {BUCKETS.map(b => <Kpi key={b} label={`${b} days`} value={fmtINR(data.grand.buckets[b])} />)}
            </div>

            <div className="table-wrapper">
              <table className="table-base">
                <thead><tr><th></th><th>Customer</th><th className="text-right">0-30</th><th className="text-right">31-60</th><th className="text-right">61-90</th><th className="text-right">90+</th><th className="text-right">Total Due</th></tr></thead>
                <tbody>
                  {data.rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-ink-muted">No outstanding dues. 🎉</td></tr>}
                  {data.rows.map((c, i) => (
                    <React.Fragment key={c.customer}>
                      <tr className="cursor-pointer hover:bg-surface-hover" onClick={() => setOpen(o => ({ ...o, [c.customer]: !o[c.customer] }))} data-testid={`out-row-${i}`}>
                        <td className="w-8">{open[c.customer] ? <CaretDown size={13} /> : <CaretRight size={13} />}</td>
                        <td className="font-semibold">{c.customer}<span className="text-[10px] text-ink-muted ml-2">{c.invoices.length} invoice(s)</span></td>
                        {BUCKETS.map(b => <td key={b} className="text-right tabular">{c.buckets[b] > 0 ? fmtINR(c.buckets[b]) : ""}</td>)}
                        <td className="text-right tabular font-bold">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={(e) => { e.stopPropagation(); setReceiveFor(c); }}
                              className="btn-secondary !py-1 !px-2 text-[11px]" data-testid={`receive-${i}`}
                              title="Bill-wise receipt (allocate against invoices)">Receive</button>
                            {(c.buckets["61-90"] + c.buckets["90+"]) > 0 && (
                              <button onClick={(e) => remind(c.customer, e)} disabled={sending === c.customer}
                                className="btn-secondary !py-1 !px-2 text-[11px] inline-flex items-center gap-1 text-green-700"
                                title="Send WhatsApp payment reminder via Gallabox" data-testid={`remind-${i}`}>
                                <WhatsappLogo size={13} weight="fill" /> {sending === c.customer ? "Sending…" : "Remind"}
                              </button>
                            )}
                            {fmtINR(c.total_due)}
                          </div>
                        </td>
                      </tr>
                      {open[c.customer] && c.invoices.map(inv => (
                        <tr key={inv.number} className="bg-surface-muted/50" data-testid={`out-inv-${inv.number}`}>
                          <td></td>
                          <td className="text-xs font-mono pl-6">{inv.number} · {inv.date} · <span className={`status-pill ${inv.days > 90 ? "status-error" : inv.days > 60 ? "status-warning" : "status-neutral"}`}>{inv.days} days</span></td>
                          <td colSpan={4} className="text-right text-xs text-ink-muted">Invoice {fmtINR(inv.total)}</td>
                          <td className="text-right tabular text-sm font-semibold">{fmtINR(inv.balance)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {data.rows.length > 0 && (
                    <tr className="bg-surface-muted font-bold" data-testid="out-total-row">
                      <td></td><td>Grand Total</td>
                      {BUCKETS.map(b => <td key={b} className="text-right tabular">{fmtINR(data.grand.buckets[b])}</td>)}
                      <td className="text-right tabular">{fmtINR(data.grand.total_due)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      {receiveFor && <ReceiveModal customer={receiveFor} onClose={() => setReceiveFor(null)} onDone={() => { setReceiveFor(null); load(); }} />}
      <FieldStyle />
    </div>
  );
}

function ReceiveModal({ customer, onClose, onDone }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState("Bank");
  const [reference, setReference] = useState("");
  const [alloc, setAlloc] = useState(() => Object.fromEntries(customer.invoices.map(inv => [inv.id, ""])));
  const [saving, setSaving] = useState(false);
  const total = customer.invoices.reduce((s, inv) => s + (Number(alloc[inv.id]) || 0), 0);

  const autoFill = () => {
    // Tally-style oldest-first: fill each invoice's full balance
    const next = {};
    customer.invoices.forEach(inv => { next[inv.id] = inv.balance; });
    setAlloc(next);
  };
  const save = async () => {
    const allocations = customer.invoices
      .filter(inv => Number(alloc[inv.id]) > 0)
      .map(inv => ({ invoice_id: inv.id, amount: Number(alloc[inv.id]) }));
    if (!allocations.length) { toast.error("Enter an amount against at least one bill"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/payments/bulk", { payment_date: date, mode, reference, allocations });
      toast.success(`Receipt of ${fmtINR(data.total)} recorded against ${data.count} bill(s)`);
      onDone();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="receive-modal">
      <div className="bg-white rounded-lg shadow-xl p-5 w-full max-w-xl space-y-3 max-h-[85vh] overflow-auto">
        <div className="font-display font-semibold">Bill-wise Receipt — {customer.customer}</div>
        <div className="grid grid-cols-3 gap-3">
          <div><div className="label-overline mb-1">Date</div><input type="date" className="field" value={date} onChange={e => setDate(e.target.value)} data-testid="rcv-date" /></div>
          <div><div className="label-overline mb-1">Mode</div>
            <select className="field" value={mode} onChange={e => setMode(e.target.value)} data-testid="rcv-mode">
              {["Bank", "Cash", "UPI", "Cheque"].map(m => <option key={m}>{m}</option>)}
            </select></div>
          <div><div className="label-overline mb-1">Reference</div><input className="field" value={reference} onChange={e => setReference(e.target.value)} data-testid="rcv-ref" /></div>
        </div>
        <div className="flex items-center justify-between">
          <div className="label-overline">Allocate against bills (Agst Ref)</div>
          <button onClick={autoFill} className="btn-ghost text-xs" data-testid="rcv-autofill">Fill full balances</button>
        </div>
        <div className="space-y-1.5">
          {customer.invoices.map((inv, j) => (
            <div key={inv.id} className="flex items-center gap-3 border border-border rounded-md px-3 py-2 text-sm">
              <div className="flex-1"><span className="font-mono font-semibold">{inv.number}</span><span className="text-[10px] text-ink-muted ml-2">{inv.date} · {inv.days}d · due {fmtINR(inv.balance)}</span></div>
              <input type="number" step="0.01" min="0" placeholder="0.00" className="field !w-32 text-right"
                value={alloc[inv.id]} onChange={e => setAlloc({ ...alloc, [inv.id]: e.target.value })} data-testid={`rcv-alloc-${j}`} />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-border pt-3">
          <div className="font-semibold">Total Receipt: <span className="tabular" data-testid="rcv-total">{fmtINR(total)}</span></div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-secondary" data-testid="rcv-cancel">Cancel</button>
            <button onClick={save} disabled={saving || total <= 0} className="btn-primary" data-testid="rcv-save">{saving ? "Saving…" : "Record Receipt"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`card-base p-3 ${accent ? "bg-brand text-white" : ""}`}>
      <div className={`text-[10px] uppercase tracking-wide ${accent ? "text-white/70" : "text-ink-muted"}`}>{label}</div>
      <div className="font-display text-lg font-bold tabular">{value}</div>
    </div>
  );
}
